import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Divider,
  Autocomplete
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { employeeMessageService } from '../../services/employeeMessageService';
import { employeeService } from '../../services/employeeService';
import { formatDateDisplay } from '../../utils/dateUtils';

const STATUS_OPTIONS = [
  { value: 'unread', label: 'Unread' },
  { value: '', label: 'All' },
  { value: 'read', label: 'Read' },
  { value: 'archived', label: 'Archived' }
];

const statusColor = (status) => {
  if (status === 'unread') return 'warning';
  return 'default';
};

const employeeName = (row) =>
  [row.first_name, row.last_name].filter(Boolean).join(' ').trim() || `Employee #${row.employee_id}`;

const authorName = (row) =>
  [row.author_first_name, row.author_last_name].filter(Boolean).join(' ').trim() || 'Unknown';

const EmployeeMessagesPanel = () => {
  const [threads, setThreads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('unread');
  const [deleteRow, setDeleteRow] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeEmployee, setComposeEmployee] = useState(null);
  const [composeText, setComposeText] = useState('');
  const [composing, setComposing] = useState(false);
  const [replyForId, setReplyForId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await employeeMessageService.list({
        status: statusFilter || undefined,
        limit: 200
      });
      setThreads(res.data ?? []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await employeeService.getAll({ activeOnly: 'true', limit: 500 });
        const list = res.data ?? res ?? [];
        setEmployees(Array.isArray(list) ? list : []);
      } catch {
        /* employee picker optional */
      }
    })();
  }, []);

  const notifyUpdated = () => {
    window.dispatchEvent(new CustomEvent('employee-messages-updated'));
  };

  const setStatus = async (row, status) => {
    setBusyId(row.id);
    setError('');
    try {
      await employeeMessageService.update(row.id, { status });
      notifyUpdated();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update message');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow) return;
    setBusyId(deleteRow.id);
    setError('');
    try {
      await employeeMessageService.delete(deleteRow.id);
      setDeleteRow(null);
      notifyUpdated();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete message');
    } finally {
      setBusyId(null);
    }
  };

  const handleCompose = async (e) => {
    e.preventDefault();
    if (!composeEmployee || !composeText.trim()) return;
    setComposing(true);
    setError('');
    setSuccess('');
    try {
      await employeeMessageService.create({
        employee_id: composeEmployee.id,
        message: composeText.trim()
      });
      setComposeOpen(false);
      setComposeEmployee(null);
      setComposeText('');
      setSuccess('Message sent.');
      notifyUpdated();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send message');
    } finally {
      setComposing(false);
    }
  };

  const handleReply = async (rootId) => {
    if (!replyText.trim()) return;
    setReplying(true);
    setError('');
    setSuccess('');
    try {
      await employeeMessageService.reply(rootId, { message: replyText.trim() });
      setReplyForId(null);
      setReplyText('');
      setSuccess('Reply sent.');
      notifyUpdated();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to reply');
    } finally {
      setReplying(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Employee messages
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<Refresh />} onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Add />}
            onClick={() => setComposeOpen(true)}
            disabled={composeOpen}
          >
            Message employee
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Notes from the shop — clock issues, supply needs, reminders. Reply in the thread or start a new message to
        someone.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {composeOpen && (
        <Paper sx={{ p: 2, mb: 2 }} component="form" onSubmit={handleCompose}>
          <Autocomplete
            size="small"
            options={employees}
            getOptionLabel={(opt) =>
              `${[opt.first_name, opt.last_name].filter(Boolean).join(' ')}${
                opt.employee_number ? ` (${opt.employee_number})` : ''
              }`
            }
            value={composeEmployee}
            onChange={(_, value) => setComposeEmployee(value)}
            renderInput={(params) => <TextField {...params} label="Employee" required />}
            sx={{ mb: 2 }}
          />
          <TextField
            required
            fullWidth
            size="small"
            label="Message"
            multiline
            minRows={3}
            value={composeText}
            onChange={(e) => setComposeText(e.target.value.slice(0, 1000))}
            inputProps={{ maxLength: 1000 }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={() => {
                setComposeOpen(false);
                setComposeEmployee(null);
                setComposeText('');
              }}
              disabled={composing}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={composing || !composeEmployee || !composeText.trim()}>
              {composing ? 'Sending…' : 'Send'}
            </Button>
          </Box>
        </Paper>
      )}

      <FormControl size="small" sx={{ minWidth: 140, mb: 1.5 }}>
        <InputLabel>Status</InputLabel>
        <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          {STATUS_OPTIONS.map((o) => (
            <MenuItem key={o.value || 'all'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : threads.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No messages.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {threads.map((root) => {
            const replies = root.replies || [];
            const archived = root.status === 'archived';
            return (
              <Paper key={root.id} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="subtitle2">
                    With {employeeName(root)}
                    {root.employee_number ? ` (${root.employee_number})` : ''}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      · {authorName(root)}
                      {root.submitted_at ? ` · ${formatDateDisplay(root.submitted_at)}` : ''}
                    </Typography>
                  </Typography>
                  <Chip size="small" label={root.status} color={statusColor(root.status)} />
                </Box>
                <Typography sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{root.message}</Typography>

                {replies.map((reply) => (
                  <Box
                    key={reply.id}
                    sx={{
                      ml: 2,
                      pl: 1.5,
                      borderLeft: '2px solid',
                      borderColor: 'divider',
                      py: 1
                    }}
                  >
                    <Typography variant="subtitle2">
                      {authorName(reply)}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {reply.submitted_at ? formatDateDisplay(reply.submitted_at) : ''}
                      </Typography>
                      {reply.status === 'unread' && Number(reply.author_id) === Number(reply.employee_id) && (
                        <Chip size="small" label="unread" color="warning" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{reply.message}</Typography>
                  </Box>
                ))}

                <Divider sx={{ my: 1.5 }} />
                {!archived && replyForId === root.id ? (
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      placeholder="Write a reply…"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value.slice(0, 1000))}
                      inputProps={{ maxLength: 1000 }}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setReplyForId(null);
                          setReplyText('');
                        }}
                        disabled={replying}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={replying || !replyText.trim()}
                        onClick={() => handleReply(root.id)}
                      >
                        {replying ? 'Sending…' : 'Send reply'}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {!archived && (
                      <Button size="small" variant="contained" onClick={() => setReplyForId(root.id)}>
                        Reply
                      </Button>
                    )}
                    {root.status === 'unread' ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busyId === root.id}
                        onClick={() => setStatus(root, 'read')}
                      >
                        Mark read
                      </Button>
                    ) : (
                      !archived && (
                        <Button
                          size="small"
                          variant="text"
                          disabled={busyId === root.id}
                          onClick={() => setStatus(root, 'unread')}
                        >
                          Mark unread
                        </Button>
                      )
                    )}
                    {!archived && (
                      <Button
                        size="small"
                        variant="text"
                        disabled={busyId === root.id}
                        onClick={() => setStatus(root, 'archived')}
                      >
                        Archive
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={busyId === root.id}
                      onClick={() => setDeleteRow(root)}
                    >
                      Delete
                    </Button>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      )}

      <Dialog open={!!deleteRow} onClose={() => !busyId && setDeleteRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete conversation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteRow
              ? `Permanently delete the conversation with ${employeeName(deleteRow)} (including replies)? This cannot be undone.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={!!busyId}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={!!busyId}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeMessagesPanel;
